import { useEffect, useState, type CSSProperties } from "react";
import { ctaAnalyticsApi, getIpAdress } from "../../api/profile";
import { useQuery } from "../../hooks/useQuery";

/**
 * Custom URL scheme the mobile app registers with the OS.
 * Same scheme already used for UAEPass deep links (see UAEPassRedirect.tsx).
 */
const APP_SCHEME = "falektayyeb://";

/** Where to send users who don't have the app installed. */
const IOS_STORE_URL = "https://apps.apple.com/ae/app/falek-tayyeb/id6471679752";
const ANDROID_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.falektayyeb.ft&pcampaignid=web_share";

/**
 * Translate the email's `target` query param into an in-app deep-link path.
 *
 * IMPORTANT: the paths below MUST match the routes the mobile app registers for
 * the `falektayyeb://` scheme. Confirm these with the mobile team. The format
 * mirrors the existing UAEPass link: `falektayyeb://<path>?<query>`.
 *
 * Returns `null` when there is no in-app target — i.e. a plain "download the
 * app" link that should go straight to the store (the original behaviour).
 */
function buildDeepLinkPath(
  target: string | null,
  params: URLSearchParams
): string | null {
  switch (target) {
    case "application": {
      const applicationId = params.get("applicationId");
      return applicationId
        ? `application?applicationId=${encodeURIComponent(applicationId)}`
        : "applications";
    }
    // Add more targets here as the emails start linking to them, e.g.:
    // case "job": {
    //   const jobId = params.get("jobId");
    //   return jobId ? `job?jobId=${encodeURIComponent(jobId)}` : "jobs";
    // }
    default:
      return null;
  }
}

const RedirectEmarati = () => {
  const searchParams = useQuery();

  const campaignName = searchParams.get("utm_campaign");
  const userIdentifier = searchParams.get("userIdentifier");
  const target = searchParams.get("target");

  const ipAddress = localStorage.getItem("ipAddress") || "";

  // The deep link we attempt; also used to power the manual "Open app" button.
  const [deepLink, setDeepLink] = useState("");
  const [showManualButton, setShowManualButton] = useState(false);
  const [statusMessage, setStatusMessage] = useState("Opening Forsatak...");

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isiOS = /iphone|ipad|ipod/.test(userAgent);
    const storeUrl = isiOS ? IOS_STORE_URL : ANDROID_STORE_URL;

    const run = async () => {
      // --- analytics (unchanged behaviour) ---
      await getIpAdress();
      ctaAnalyticsApi({
        campaignName: campaignName,
        pageUrl: "https://falektayyeb.com/redirect",
        ipAddress: ipAddress,
        userAgent: userAgent,
        userIdentifier: userIdentifier || "1",
      });

      const path = buildDeepLinkPath(target, searchParams);

      // No in-app target → this is a "download the app" link. Go to the store,
      // exactly like before.
      if (!path) {
        window.open(storeUrl, "_self");
        return;
      }

      // We have a target → try to open the app, then fall back to the store.
      const deepLinkUrl = `${APP_SCHEME}${path}`;
      setDeepLink(deepLinkUrl);
      setStatusMessage("Opening the Forsatak app...");

      // 1) Attempt to open the app directly.
      window.location.href = deepLinkUrl;

      // 2) Hidden iframe attempt — helps trigger the app on some Android browsers.
      const iframeTimer = window.setTimeout(() => {
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = deepLinkUrl;
        document.body.appendChild(iframe);
      }, 100);

      // 3) Offer a manual button in case the browser blocked the auto-open.
      const manualTimer = window.setTimeout(() => {
        setShowManualButton(true);
        setStatusMessage("Didn't open? Tap the button below.");
      }, 2000);

      // 4) The key heuristic: if the app opened, this browser tab is now in the
      //    background, so `document.hidden` is true and we leave it alone.
      //    If the page is STILL visible after the delay, the app isn't installed
      //    (or didn't open) → send the user to the store.
      const storeFallbackTimer = window.setTimeout(() => {
        if (!document.hidden) {
          window.open(storeUrl, "_self");
        }
      }, 2500);

      // Cleanup if the component unmounts before the timers fire.
      return () => {
        window.clearTimeout(iframeTimer);
        window.clearTimeout(manualTimer);
        window.clearTimeout(storeFallbackTimer);
      };
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={styles.body}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={styles.container}>
        <h2>Forsatak</h2>
        <div style={styles.loader} />
        <p>{statusMessage}</p>
        {showManualButton && deepLink && (
          <a href={deepLink} style={styles.button}>
            Open Forsatak App
          </a>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: CSSProperties } = {
  body: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    margin: 0,
    backgroundColor: "#f5f5f5",
  },
  container: {
    textAlign: "center",
    padding: 40,
    background: "white",
    borderRadius: 10,
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    maxWidth: 400,
  },
  loader: {
    border: "3px solid #f3f3f3",
    borderTop: "3px solid #007B3A",
    borderRadius: "50%",
    width: 50,
    height: 50,
    animation: "spin 1s linear infinite",
    margin: "20px auto",
  },
  button: {
    backgroundColor: "#007B3A",
    color: "white",
    padding: "12px 24px",
    textDecoration: "none",
    borderRadius: 6,
    display: "inline-block",
    marginTop: 20,
  },
};

export default RedirectEmarati;
